import { Button, Icon, Tooltip } from "materia"
import type { PropsWithChildren, ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { modifiers } from "ui-tools"
import { ProductModal } from "../ProductModal"
import s from "./ProductModalActivity.module.scss"

export const ProductModalActivity = ({ children }: PropsWithChildren) => (
  <ProductModal.Side>
    <div className={s.modal}>{children}</div>
  </ProductModal.Side>
)

type TopProps = {
  completedCount: number
  totalCount: number
  isLocked: boolean
}
const Top = ({ completedCount, totalCount, isLocked }: TopProps) => {
  const { t } = useTranslation()
  return (
    <div
      className={modifiers(s, "top", {
        isCompleted: completedCount >= totalCount,
        isLocked,
      })}
    >
      <h3
        aria-live="polite"
        aria-label={t("activity.TASK_COMPLETION_RATE", {
          value: completedCount,
          total: totalCount,
        })}
        className={s.top__progressFraction}
      >
        {completedCount}/{totalCount}
      </h3>
      <ProgressBar
        value={completedCount}
        maxValue={totalCount}
        isLocked={isLocked}
      />
    </div>
  )
}

const Tasks = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation()
  return (
    <ul
      className={s.tasks}
      aria-label={t("activity.TASKS")}
      role="group"
      tabIndex={0}
    >
      {children}
    </ul>
  )
}

type TaskProps = {
  title: string
  isChecked: boolean
  isLocked: boolean
  isRejected: boolean
  hasAddedAttachments: boolean
  showAttachmentButton: boolean
  onClick: () => void
  onAttachmentsClick: () => void
}
const Task = ({
  title,
  isChecked,
  isLocked,
  isRejected,
  hasAddedAttachments,
  showAttachmentButton,
  onClick,
  onAttachmentsClick,
}: TaskProps) => {
  const { t } = useTranslation()
  return (
    <li className={s.task}>
      <label
        className={modifiers(s, "taskCheckbox", {
          isLocked,
          isChecked,
          isRejected,
        })}
      >
        <div className={s.taskCheckbox__icon}>
          {(isChecked || isLocked) && (
            <Icon icon="CheckmarkCircle" size="extraLarge" />
          )}
        </div>
        {title}
        <input
          onChange={onClick}
          type="checkbox"
          disabled={isLocked}
          className={s.taskCheckbox__input}
        />
      </label>
      {showAttachmentButton && (
        <button
          className={modifiers(s, "taskAttachmentsButton", {
            blue: hasAddedAttachments,
            red: isRejected,
          })}
          type="button"
          aria-label={t("activity.ATTACHMENTS")}
          title={t("activity.ATTACHMENTS")}
          onClick={onAttachmentsClick}
        >
          <Icon icon="Attachment" size="extraLarge" />
        </button>
      )}
    </li>
  )
}

type BottomProps = {
  isVisible: boolean
  children: ReactNode
}
const Bottom = ({ isVisible, children }: BottomProps) => {
  if (!isVisible) return null

  return (
    <ProductModal.ActionButtons>
      <div className={s.bottom} aria-live="polite">
        {children}
      </div>
    </ProductModal.ActionButtons>
  )
}

type ConfirmButtonProps = {
  confirmText: string
  onClick: () => void
  isVisible: boolean
}
const ConfirmButton = ({
  isVisible,
  confirmText,
  onClick,
}: ConfirmButtonProps) => {
  if (!isVisible) return null

  return (
    <Button borderRadius="12" size="big" onClick={onClick}>
      {confirmText}
    </Button>
  )
}

// TODO: Waiting for approval looks bad on mobile. Background isn't filled
type WaitingForApprovalProps = {
  title: string
  tooltipTitle: string
  tooltipDescription: string
  isVisible: boolean
}
const WaitingForApproval = ({
  title,
  tooltipTitle,
  tooltipDescription,
  isVisible,
}: WaitingForApprovalProps) => {
  if (!isVisible) return null

  return (
    <div className={s.waitingForApproval}>
      <Icon icon="ClockLight" size="large" />
      <div className={s.waitingForApproval__title}>{title}</div>

      <HelpTooltip title={tooltipTitle} description={tooltipDescription} />
    </div>
  )
}

type HelpTooltipProps = {
  title: string
  description: string
}
const HelpTooltip = ({ title, description }: HelpTooltipProps) => {
  const { t } = useTranslation()
  return (
    <Tooltip position="top">
      <Tooltip.Trigger className={s.helpTooltip__icon}>
        <Icon icon="HelpCircle" title={t("activity.HELP")} />
      </Tooltip.Trigger>
      <Tooltip.Content>
        <div className={s.helpTooltip__content}>
          <div className={s.helpTooltip__title}>{title}</div>
          <div className={s.helpTooltip__description}>{description}</div>
        </div>
      </Tooltip.Content>
    </Tooltip>
  )
}

const Skeleton = () => (
  <ProductModal.Side>
    <div className={s.skeleton} />
  </ProductModal.Side>
)

ProductModalActivity.Top = Top
ProductModalActivity.Tasks = Tasks
ProductModalActivity.Task = Task
ProductModalActivity.Bottom = Bottom
ProductModalActivity.ConfirmButton = ConfirmButton
ProductModalActivity.WaitingForApproval = WaitingForApproval
ProductModalActivity.Skeleton = Skeleton

type ProgressBarProps = {
  value: number
  isLocked: boolean
  maxValue?: number
}
const ProgressBar = ({ value, maxValue = 100, isLocked }: ProgressBarProps) => (
  <div className={modifiers(s, "progressBar", { isLocked })}>
    <div
      className={s.progressBar__progress}
      style={{ transform: `scaleX(${value / maxValue})` }}
    />
  </div>
)
